import { FC, useMemo } from 'react'
import { useQuery } from '@apollo/client'

import { useDataLayer, useQueryParams } from 'hooks'
import {
  calculateQtyValuesFromMinMaxSplit,
  filterPackagesByQtyFilter,
  sortPackagesByPrice,
} from 'components/contexts/event-page-filters/utils'
import { useActions as useEventPageActions } from 'components/contexts/event-page'
import {
  useActions as useFilterActions,
  useStore as useFilterStore,
} from 'components/contexts/event-page-filters'
import { ArtworkNode, GetEventAuctionByIdQuery } from 'gql'
import { Flex, Grid, NoData, Spinner } from 'components/ui'
import { formatCurrency, getIdFromSlug } from 'utils'
import { GRANTED_PACKAGES } from 'gql/platform/queries'
import { GET_EVENT_AUCTION_BY_ID } from 'gql/artwork/queries'
import Package from './package'

type EventSideMenuProps = Pick<ArtworkNode, 'eventExtendedStatus' | 'salesOnDateLocal'>

const PackagesWrapper: FC<EventSideMenuProps & { artworkSlug: string }> = ({
  eventExtendedStatus,
  salesOnDateLocal,
  artworkSlug,
}) => {
  const {
    query: { quantity },
  } = useQueryParams()
  const { sort } = useFilterStore()

  const { data, loading } = useQuery(GRANTED_PACKAGES, {
    variables: { artworkSlug, isTicket: false },
  })

  const { data: eventData } = useQuery<GetEventAuctionByIdQuery>(GET_EVENT_AUCTION_BY_ID, {
    variables: { pk: getIdFromSlug(artworkSlug) },
  })

  const filteredData = useMemo(
    () =>
      sortPackagesByPrice(
        filterPackagesByQtyFilter(data?.allPackages.edges.map(({ node }) => node) || [], quantity),
        sort
      ),
    [quantity, data?.allPackages, sort]
  )

  const dl = useDataLayer()

  const { setFormQuantityOptions } = useFilterActions()

  const { onAddToCheckout } = useEventPageActions()

  if (loading) {
    return <Spinner center />
  }

  if (!filteredData?.length || eventExtendedStatus === 'started') {
    return (
      <Flex justifyContent="center" alignItems="center" height="100%">
        <NoData description="No VIP packages available" />
      </Flex>
    )
  }

  return (
    <Grid rowGap={24} padding="0 16px">
      {filteredData.map((item, i) => (
        <Package
          availableQuantity={item.availableQuantity}
          shortDescription={item.shortDescription}
          longDescription={item.longDescription}
          maxSplit={item.maxSplit}
          price={item.price}
          image={item.image}
          name={item.name}
          eventExtendedStatus={eventExtendedStatus}
          salesOnDateLocal={salesOnDateLocal}
          defaultOpen={i === 0}
          onClick={() => {
            const availableQuantity = Math.min(item.maxSplit, item.availableQuantity)

            setFormQuantityOptions(
              calculateQtyValuesFromMinMaxSplit(
                Math.min(item.minSplit, availableQuantity),
                availableQuantity
              )
            )

            onAddToCheckout({
              title: item.name,
              price: formatCurrency(item.price, 0),
              unformatedPrice: item.price,
              deliveryNotes: item.deliveryNotes,
              longDescription: item.longDescription,
              messages: [],
              checkoutPayload: {
                packageId: item.id,
              },
            })
          }}
          onOpen={() => {
            const event = eventData.findArtwork || ({} as GetEventAuctionByIdQuery['findArtwork'])

            dl.push({
              event: 'view_item',
              ecommerce: {
                item_list_name: 'packages',
                currency: 'USD',
                value: item.price,
                items: [
                  {
                    item_name: item.name,
                    item_id: item.pk,
                    star_name: event.mainArtist?.fullName,
                    event_date: event.eventStartDateLocal,
                    venue_name: event.venue?.name,
                    tour_name: event.tour?.title,
                    event_name: event.title,
                  },
                ],
              },
            })
          }}
          // eslint-disable-next-line react/no-array-index-key
          key={`${item.id}${i}`}
        />
      ))}
    </Grid>
  )
}

export default PackagesWrapper
